import { stringifyEntityRef } from "@backstage/catalog-model";
import { EmptyState, InfoCard, Progress } from "@backstage/core-components";
import { useApi } from "@backstage/core-plugin-api";
import { useEntity } from "@backstage/plugin-catalog-react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  Link as MuiLink,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import CloudQueueIcon from "@material-ui/icons/CloudQueue";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import LaunchIcon from "@material-ui/icons/Launch";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { explorerApiRef } from "../../api/ExplorerApi";
import type {
  Assignment,
  CloudKey,
  CloudProvider,
  ExplorerAccountAssetsResponse,
  ExplorerAsset,
  ExplorerServiceGroup,
} from "../../api/types";
import { AssetInfoDrawer } from "../AssetExplorerPage/AssetInfoDrawer";
import type { AssetTableRow, TreeNodeModel } from "../AssetExplorerPage/types";
import { AssignmentBadges } from "../shared/AssignmentBadges";

const useStyles = makeStyles((theme) => ({
  intro: {
    marginBottom: theme.spacing(3),
  },
  introBody: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  helperCopy: {
    color: theme.palette.text.secondary,
    maxWidth: 780,
  },
  cards: {
    marginTop: theme.spacing(2),
  },
  accountCard: {
    border: `1px solid ${theme.palette.divider}`,
  },
  directAssetCard: {
    height: "100%",
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    background: theme.palette.background.paper,
  },
  directAssetHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginBottom: theme.spacing(1.25),
  },
  directAssetService: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    borderRadius: 999,
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.action.hover,
    fontWeight: 700,
  },
  directAssetTitle: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
    overflowWrap: "anywhere",
  },
  directAssetMeta: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.5),
  },
  directAssetMetaLine: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(0.75),
    minWidth: 0,
    "& $mono": {
      minWidth: 0,
    },
  },
  directAssetMetaLabel: {
    color: theme.palette.text.secondary,
    minWidth: 76,
    flexShrink: 0,
  },
  accountHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  titleWrap: {
    minWidth: 0,
  },
  accountTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  cloudChip: {
    fontWeight: 700,
    letterSpacing: "0.04em",
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    overflowWrap: "anywhere",
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  actionWrap: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexShrink: 0,
  },
  assetsPanel: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  inlineAlert: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
  },
  group: {
    marginTop: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
  },
  groupHeader: {
    padding: theme.spacing(1.25, 1.5),
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  tableWrap: {
    overflowX: "auto",
  },
  assetMainLine: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  assetName: {
    fontWeight: 600,
  },
  assetMetaLine: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.25),
  },
  assetActions: {
    whiteSpace: "nowrap",
  },
  subdued: {
    color: theme.palette.text.secondary,
  },
  emptyInline: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

type AccountAssetsState = {
  expanded: boolean;
  loading: boolean;
  error?: string;
  data?: ExplorerAccountAssetsResponse;
};

type ParsedAssetAssignment = {
  accountId?: string;
  assetStableId?: string;
  assetType?: string;
  serviceLabel: string;
};

const ASSET_TYPE_LABELS: Record<string, string> = {
  AwsDynamoDbTable: "AWS DynamoDB Table",
  AwsElasticFileSystem: "AWS Elastic File System",
  AwsInstance: "AWS EC2 Instance",
  AwsLambdaFunction: "AWS Lambda Function",
  AwsRdsInstance: "AWS RDS Instance",
  AwsS3Bucket: "AWS S3 Bucket",
  AwsVolume: "AWS EBS Volume",
  AzureAppService: "Azure App Service",
  AzureKeyVault: "Azure Key Vault",
  AzureKubernetesService: "Azure Kubernetes Service",
  AzureManagedDisk: "Azure Managed Disk",
  AzureSqlDatabase: "Azure SQL Database",
  AzureStorage: "Azure Storage Account",
  AzureStorageBlobContainer: "Azure Blob Container",
  AzureVm: "Azure VM",
  AzureVmScaleSet: "Azure VM Scale Set",
  GcpBigQueryDataset: "GCP BigQuery Dataset",
  GcpBigQueryTable: "GCP BigQuery Table",
  GcpCloudRunService: "GCP Cloud Run Service",
  GcpCloudSqlInstance: "GCP Cloud SQL Instance",
  GcpComputeDisk: "GCP Compute Disk",
  GcpComputeInstance: "GCP VM Instance",
  GcpGkeCluster: "GCP GKE Cluster",
  GcpStorageBucket: "GCP Cloud Storage Bucket",
};

function toCloudProvider(cloud: CloudKey): CloudProvider {
  if (cloud === "gcp") return "GCP";
  if (cloud === "aws") return "AWS";
  return "Azure";
}

function formatDate(value?: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function decodeNodeRefSegment(segment: string): string | undefined {
  try {
    return decodeURIComponent(segment);
  } catch {
    return undefined;
  }
}

function humanizeAssetType(assetType?: string): string {
  if (!assetType) return "Asset cloud";
  const knownLabel = ASSET_TYPE_LABELS[assetType];
  if (knownLabel) return knownLabel;

  return assetType
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\bGcp\b/g, "GCP")
    .replace(/\bAws\b/g, "AWS")
    .replace(/\bSql\b/g, "SQL")
    .replace(/\bDb\b/g, "DB")
    .replace(/\bVm\b/g, "VM")
    .replace(/\bDns\b/g, "DNS")
    .replace(/\bGke\b/g, "GKE")
    .replace(/\bKms\b/g, "KMS")
    .replace(/\bIp\b/g, "IP")
    .replace(/\bVpc\b/g, "VPC")
    .replace(/\bEks\b/g, "EKS")
    .replace(/\bRds\b/g, "RDS")
    .replace(/\bS3\b/g, "AWS S3");
}

function getAssetAssignmentSummary(
  assignment: Assignment,
): ParsedAssetAssignment {
  const match = /^cloudhealth:v1:(gcp|aws|azure):asset:(.+):(.+):(.+)$/.exec(
    assignment.nodeRef,
  );

  if (!match) {
    return { serviceLabel: "Asset cloud" };
  }

  const accountId = decodeNodeRefSegment(match[2]);
  const assetType = decodeNodeRefSegment(match[3]);
  const assetStableId = decodeNodeRefSegment(match[4]);

  return {
    accountId,
    assetStableId,
    assetType,
    serviceLabel: humanizeAssetType(assetType),
  };
}

function toTreeAssetFromExplorerAsset(
  asset: ExplorerAsset,
  group: ExplorerServiceGroup,
  cloud: CloudProvider,
): TreeNodeModel {
  return {
    id: asset.id,
    level: "asset",
    label: asset.name,
    cloud,
    status: "collapsed",
    childIds: [],
    assetCategory: group.serviceCategory,
    assetType: asset.assetType,
    assetStatus: asset.status,
    assetLocation: asset.location,
    nodeRef: asset.nodeRef,
    parentNodeRef: asset.parentNodeRef,
    resourceGroup: asset.resourceGroup,
    costCapability: asset.costCapability,
  };
}

function toAssetInfoRowFromExplorerAsset(
  asset: ExplorerAsset,
  group: ExplorerServiceGroup,
  data: ExplorerAccountAssetsResponse,
): AssetTableRow {
  const accountLabel =
    data.cloud === "Azure"
      ? (asset.resourceGroup ?? data.displayName)
      : data.displayName;

  return {
    id: asset.nodeRef ?? asset.id,
    asset: toTreeAssetFromExplorerAsset(asset, group, data.cloud),
    assetLabel: asset.name,
    accountLabel,
    accountNodeRef: asset.parentNodeRef ?? data.nodeRef,
    serviceLabel: `${group.serviceCategory} / ${group.serviceName}`,
    category: group.serviceCategory,
    serviceName: group.serviceName,
  };
}

function toAssetInfoRowFromAssignment(assignment: Assignment): AssetTableRow {
  const summary = getAssetAssignmentSummary(assignment);
  const displayName =
    assignment.nodeDisplayName ?? summary.assetStableId ?? assignment.nodeRef;
  const cloud = toCloudProvider(assignment.cloud);

  return {
    id: assignment.nodeRef,
    asset: {
      id: assignment.nodeRef,
      level: "asset",
      label: displayName,
      cloud,
      status: "collapsed",
      childIds: [],
      assetCategory: "Asignación directa",
      assetType: summary.assetType,
      nodeRef: assignment.nodeRef,
      costCapability: "asset",
    },
    assetLabel: displayName,
    accountLabel: summary.accountId ?? assignment.accountId,
    serviceLabel: summary.serviceLabel,
    category: "Asignación directa",
    serviceName: summary.serviceLabel,
  };
}

function DirectAssetCard({
  assignment,
  onOpenAttributes,
}: {
  assignment: Assignment;
  onOpenAttributes: (row: AssetTableRow) => void;
}) {
  const classes = useStyles();
  const summary = getAssetAssignmentSummary(assignment);
  const displayName =
    assignment.nodeDisplayName ?? summary.assetStableId ?? assignment.nodeRef;
  const createdAt = formatDate(assignment.createdAt);
  const handleOpenAttributes = useCallback(() => {
    onOpenAttributes(toAssetInfoRowFromAssignment(assignment));
  }, [assignment, onOpenAttributes]);

  return (
    <Paper elevation={0} className={classes.directAssetCard}>
      <Box p={2}>
        <Box className={classes.directAssetHeader}>
          <Box minWidth={0}>
            <Typography
              variant="caption"
              className={classes.directAssetService}
            >
              <CloudQueueIcon fontSize="small" />
              Servicio · {summary.serviceLabel}
            </Typography>
            <Typography
              variant="subtitle1"
              className={classes.directAssetTitle}
            >
              {displayName}
            </Typography>
          </Box>
          <Box flexShrink={0}>
            <AssignmentBadges assignment={assignment} />
          </Box>
        </Box>

        <Box display="flex" flexWrap="wrap" gridGap={8}>
          <Chip
            size="small"
            color="primary"
            className={classes.cloudChip}
            label={assignment.cloud.toUpperCase()}
          />
          {summary.assetType && <Chip size="small" label={summary.assetType} />}
        </Box>

        <Box className={classes.directAssetMeta}>
          {summary.accountId && (
            <Box className={classes.directAssetMetaLine}>
              <Typography
                variant="caption"
                className={classes.directAssetMetaLabel}
              >
                Cuenta
              </Typography>
              <Typography className={classes.mono}>
                {summary.accountId}
              </Typography>
            </Box>
          )}
          {summary.assetStableId && (
            <Box className={classes.directAssetMetaLine}>
              <Typography
                variant="caption"
                className={classes.directAssetMetaLabel}
              >
                ID técnico
              </Typography>
              <Typography className={classes.mono}>
                {summary.assetStableId}
              </Typography>
            </Box>
          )}
          <Box className={classes.directAssetMetaLine}>
            <Typography
              variant="caption"
              className={classes.directAssetMetaLabel}
            >
              Referencia
            </Typography>
            <Typography className={classes.mono}>
              {assignment.nodeRef}
            </Typography>
          </Box>
        </Box>

        {(assignment.createdBy || createdAt) && (
          <Box mt={1.5}>
            <Typography variant="caption" className={classes.subdued}>
              {assignment.createdBy
                ? `Asignada por ${assignment.createdBy}`
                : "Asignada"}
              {createdAt ? ` · ${createdAt}` : ""}
            </Typography>
          </Box>
        )}

        <Box mt={2}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            onClick={handleOpenAttributes}
          >
            Ver atributos
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

function AssignmentAssets({
  data,
  onOpenAttributes,
}: {
  data: ExplorerAccountAssetsResponse;
  onOpenAttributes: (row: AssetTableRow) => void;
}) {
  const classes = useStyles();

  const message =
    data._message ??
    (data.truncated
      ? "La respuesta de CloudHealth fue truncada; puede haber más assets disponibles."
      : undefined);

  if (data.groups.length === 0) {
    return (
      <Box>
        {message && (
          <Typography className={classes.emptyInline} variant="body2">
            {message}
          </Typography>
        )}
        <Typography className={classes.emptyInline} variant="body2">
          No hay assets activos para esta cuenta.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {message && (
        <Box mb={1} role="status">
          <Typography variant="body2" className={classes.subdued}>
            {message}
          </Typography>
        </Box>
      )}
      <Typography variant="body2" className={classes.subdued}>
        {data.totalCount} assets activos agrupados por servicio.
      </Typography>
      {data.groups.map((group) => (
        <Paper key={group.key} elevation={0} className={classes.group}>
          <Box className={classes.groupHeader}>
            <Box>
              <Typography variant="subtitle2">
                {group.serviceCategory} / {group.serviceName}
              </Typography>
              <Typography className={classes.mono}>
                {group.assetType}
              </Typography>
            </Box>
            <Chip size="small" label={`${group.count} assets`} />
          </Box>
          <Box className={classes.tableWrap}>
            <Table size="small" aria-label={`${group.serviceName} assets`}>
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Atributos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.assets.map((asset) => {
                  const handleOpenAttributes = () => {
                    onOpenAttributes(
                      toAssetInfoRowFromExplorerAsset(asset, group, data),
                    );
                  };

                  return (
                    <TableRow key={asset.id} hover>
                      <TableCell>
                        <Box className={classes.assetMainLine}>
                          <Typography
                            variant="body2"
                            className={classes.assetName}
                          >
                            {asset.name}
                          </Typography>
                          {asset.location && (
                            <Chip size="small" label={asset.location} />
                          )}
                          {asset.resourceGroup && (
                            <Chip size="small" label={asset.resourceGroup} />
                          )}
                        </Box>
                        <Box className={classes.assetMetaLine}>
                          <Typography className={classes.mono}>
                            tipo: {asset.assetType}
                          </Typography>
                          <Typography className={classes.mono}>
                            ref: {asset.nodeRef}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{asset.status ?? "—"}</TableCell>
                      <TableCell align="right" className={classes.assetActions}>
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={handleOpenAttributes}
                        >
                          Ver atributos
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

type AssignmentCardProps = {
  assignment: Assignment;
  state?: AccountAssetsState;
  onToggleAssets: (assignment: Assignment) => void;
  onOpenAttributes: (row: AssetTableRow) => void;
};

function AssignmentCard({
  assignment,
  state,
  onToggleAssets,
  onOpenAttributes,
}: AssignmentCardProps) {
  const classes = useStyles();
  const displayName = assignment.nodeDisplayName ?? assignment.accountId;
  const panelId = `cloud-assets-${assignment.id}`;
  const createdAt = formatDate(assignment.createdAt);

  const handleToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onToggleAssets(assignment);
    },
    [assignment, onToggleAssets],
  );

  return (
    <Card elevation={0} className={classes.accountCard}>
      <CardContent>
        <Box className={classes.accountHeader}>
          <Box className={classes.titleWrap}>
            <Box className={classes.accountTitle}>
              <CloudQueueIcon color="primary" />
              <Typography variant="h6" noWrap>
                {displayName}
              </Typography>
              <Chip
                size="small"
                color="primary"
                className={classes.cloudChip}
                label={assignment.cloud.toUpperCase()}
              />
              <AssignmentBadges assignment={assignment} />
            </Box>
            <Box className={classes.metaRow}>
              <Typography className={classes.mono}>
                accountId: {assignment.accountId}
              </Typography>
              <Typography className={classes.mono}>
                nodeRef: {assignment.nodeRef}
              </Typography>
            </Box>
            {(assignment.createdBy || createdAt) && (
              <Typography variant="caption" className={classes.subdued}>
                {assignment.createdBy
                  ? `Asignada por ${assignment.createdBy}`
                  : "Asignada"}
                {createdAt ? ` · ${createdAt}` : ""}
              </Typography>
            )}
          </Box>
          <Box className={classes.actionWrap}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleToggle}
              aria-expanded={Boolean(state?.expanded)}
              aria-controls={panelId}
            >
              {state?.expanded ? "Ocultar assets" : "Ver assets"}
            </Button>
          </Box>
        </Box>

        <Collapse in={Boolean(state?.expanded)} timeout="auto" unmountOnExit>
          <Box id={panelId} className={classes.assetsPanel}>
            {state?.loading && <Progress />}
            {state?.error && (
              <Box role="alert" className={classes.inlineAlert}>
                <ErrorOutlineIcon fontSize="small" />
                <Typography variant="body2">{state.error}</Typography>
              </Box>
            )}
            {state?.data && (
              <AssignmentAssets
                data={state.data}
                onOpenAttributes={onOpenAttributes}
              />
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export function EntityCloudAssignmentsContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const api = useApi(explorerApiRef);
  const entityRef = useMemo(() => stringifyEntityRef(entity), [entity]);
  const isDomain = entity.kind.toLocaleLowerCase("en-US") === "domain";
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const accountAssignments = useMemo(
    () => assignments.filter((a) => a.nodeLevel === "account"),
    [assignments],
  );
  const assetAssignments = useMemo(
    () => assignments.filter((a) => a.nodeLevel === "asset"),
    [assignments],
  );
  const [assetsByAssignmentId, setAssetsByAssignmentId] = useState<
    Record<string, AccountAssetsState>
  >({});
  const [attributesRow, setAttributesRow] = useState<AssetTableRow>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api
      .getEntityAssets(entityRef)
      .then((response) => {
        if (!cancelled) {
          setAssignments(response.assignments);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "No se pudieron cargar las asignaciones cloud.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, entityRef]);

  const toggleAssets = useCallback(
    async (assignment: Assignment) => {
      const current = assetsByAssignmentId[assignment.id];
      const nextExpanded = !current?.expanded;

      setAssetsByAssignmentId((previous) => ({
        ...previous,
        [assignment.id]: {
          ...previous[assignment.id],
          expanded: nextExpanded,
        },
      }));

      if (!nextExpanded || current?.data || current?.loading) {
        return;
      }

      setAssetsByAssignmentId((previous) => ({
        ...previous,
        [assignment.id]: {
          ...previous[assignment.id],
          expanded: true,
          loading: true,
          error: undefined,
        },
      }));

      try {
        const data = await api.getAccountAssets(
          toCloudProvider(assignment.cloud),
          assignment.accountId,
          assignment.nodeDisplayName ?? assignment.accountId,
        );
        setAssetsByAssignmentId((previous) => ({
          ...previous,
          [assignment.id]: {
            expanded: true,
            loading: false,
            data,
          },
        }));
      } catch (err) {
        setAssetsByAssignmentId((previous) => ({
          ...previous,
          [assignment.id]: {
            ...previous[assignment.id],
            expanded: true,
            loading: false,
            error:
              err instanceof Error
                ? err.message
                : "No se pudieron cargar los assets de esta cuenta.",
          },
        }));
      }
    },
    [api, assetsByAssignmentId],
  );

  const openAttributes = useCallback((row: AssetTableRow) => {
    setAttributesRow(row);
  }, []);

  const closeAttributes = useCallback(() => {
    setAttributesRow(undefined);
  }, []);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <Box role="alert" className={classes.inlineAlert}>
        <ErrorOutlineIcon fontSize="small" />
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (assignments.length === 0) {
    return (
      <EmptyState
        missing="content"
        title="Sin cuentas ni assets cloud asignados"
        description={
          isDomain
            ? "Este dominio todavía no tiene Systems hijos con cloudhealth.io/asset-refs."
            : "System y Component muestran asignaciones directas desde catalog-info.yaml con cloudhealth.io/asset-refs."
        }
        action={
          <Button
            component={RouterLink}
            to="/cloud-explorer"
            color="primary"
            variant="contained"
            endIcon={<LaunchIcon />}
          >
            Asigna cuentas y assets desde Cloud Explorer
          </Button>
        }
      />
    );
  }

  return (
    <Box>
      <InfoCard
        title={
          isDomain
            ? "Cloud assets de los Systems del dominio"
            : "Asignaciones directas a esta entidad"
        }
        className={classes.intro}
      >
        <Box className={classes.introBody}>
          <Typography variant="body2" className={classes.helperCopy}>
            {isDomain
              ? "Incluye las asignaciones cloud declaradas en los Systems hijos del dominio mediante cloudhealth.io/asset-refs. Usa Ver atributos para consultar el inventario completo de un asset solo cuando lo necesites."
              : "Asignaciones directas a esta entidad desde catalog-info.yaml (cloudhealth.io/asset-refs). Usa Ver atributos para consultar el inventario completo de un asset solo cuando lo necesites."}
          </Typography>
          <MuiLink component={RouterLink} to="/cloud-explorer">
            Gestionar en Cloud Explorer
          </MuiLink>
        </Box>
      </InfoCard>

      {accountAssignments.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            Cuentas asignadas ({accountAssignments.length})
          </Typography>
          <Grid container spacing={2} className={classes.cards}>
            {accountAssignments.map((assignment) => (
              <Grid item xs={12} key={assignment.id}>
                <AssignmentCard
                  assignment={assignment}
                  state={assetsByAssignmentId[assignment.id]}
                  onToggleAssets={toggleAssets}
                  onOpenAttributes={openAttributes}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {assetAssignments.length > 0 && (
        <Box mt={assetAssignments.length > 0 ? 3 : 0}>
          <Typography variant="h6" gutterBottom>
            Assets asignados directamente ({assetAssignments.length})
          </Typography>
          <Grid container spacing={2}>
            {assetAssignments.map((assignment) => (
              <Grid item xs={12} sm={6} md={4} key={assignment.id}>
                <DirectAssetCard
                  assignment={assignment}
                  onOpenAttributes={openAttributes}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Divider />
      <AssetInfoDrawer
        open={Boolean(attributesRow)}
        row={attributesRow}
        onClose={closeAttributes}
        initialAttributesExpanded
      />
    </Box>
  );
}
